import React, { useState, useEffect } from "react";
import { Plus, TrendingUp, X, Check, AlertCircle } from "lucide-react";
import { ReactComponent as Logo } from "../../assets/icons/svg/triva_logo.svg";
import { ReactComponent as Star } from "../../assets/icons/svg/triva_premium-icon.svg";
import { ReactComponent as LiteBgImageIcon } from "../../assets/icons/svg/Triva_lite_desktop.svg";
import { ReactComponent as SparkBgImageIcon } from "../../assets/icons/svg/triva_spark_desktop.svg";
import { ReactComponent as IgniteBgImageIcon } from "../../assets/icons/svg/triva_ignite_desktop.svg";
import { ReactComponent as ThriveBgImageIcon } from "../../assets/icons/svg/triva_thrive_desktop.svg";
import { ReactComponent as SignatureBgImageIcon } from "../../assets/icons/svg/triva_premium_desktop.svg";
import { ReactComponent as ArrowUp } from "../../assets/icons/svg/arrow_up.svg";
import { ReactComponent as ArrowDown } from "../../assets/icons/svg/arrow_down.svg";
import { ReactComponent as BgIllustration } from "../../assets/icons/svg/triva-plan-image.svg";

import { API_URLS } from "../api/urls";
import ExpertConnectionForm from "./Expert_Connection_Form";
import LoginForm from "./auth/Login_Form";
import OTPVerification from "./auth/OTP_Verification";
import GradientBorderPremiumTag from "../ui/Gradient_Border_Premium_Tag";
import UserRegistration from "./auth/User_Registration";
import RecommendedPlanDetailsForDesktop from "./Recomanded_Plan_deails_Page";
import PaymentSuccessPageDesktop from "./Payment_Success_Page_Desktop";
import { AuthStorage } from "./auth/AuthStorage";
import TrivaSignaturePlanSuccessPage from "./Triva_Signature_Plan_Success_Page";
import {
  SIGNATURE_PLAN_ID,
  SIGNATURE_PLAN_TITLE,
  SIGNATURE_PLAN_SUBTITLE,
  SIGNATURE_PLAN_DESCRIPTION,
  SIGNATURE_PLAN_ADDITIONAL_INFO,
  SIGNATURE_PLAN_PERFECT_FOR,
  SIGNATURE_PLAN_WHAT_YOU_GET,
} from "../data/signaturePlanData";

interface PlanCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  bgColor: string;
  borderColor?: string;
  issignature?: boolean;
  onSelect: () => void;
  price?: string;
  originalPrice?: string;
}

interface TrivaPlansDesktopProps {
  userId: number | null;
  directPurchasePlanId?: number;
  deeplinkPlan?: string | null;
}

// API Response Types
interface ApiPlanData {
  subscription_plan_id: number;
  subscription_plan_name: string;
  std_subscription_perks: string;
  duration_and_price_and_perks: {
    standard_details_id: number;
    std_subscription_price: string;
    std_subscription_discount_price: string;
    duration_period: string;
    product_id: string;
    std_subscription_description: string;
    std_subscription_perfect_for: string;
    std_subscription_not_included: string;
    std_subscription_features: string;
  }[];
}

interface ApiResponse {
  success: string;
  message: string;
  plans: ApiPlanData[];
}

// Login Data Interface
interface LoginData {
  Success: string;
  message: string;
  user_id: number;
  otp?: number;
  random_gen_no?: string;
  user_status: string;
  remaining_days: number;
  access_token: string;
  refresh_token: string;
  contactInfo: string;
  contactType: "phone" | "email";
  isNewUser?: boolean;
  isGoogleAuthenticated?: boolean;
}

// User Data Interface from OTP verification
interface UserData {
  user_id: number;
  user_mobileno: string;
  otp: string;
  user_fname: string;
  user_lname: string;
  goal: string;
  gender: string;
  age: number;
  city: string;
  state: string;
  dob: string;
  height_parameter: string;
  height_value: number;
  weight_parameter: string;
  weight_value: number;
  target_weight: number;
  target_weight_parameter: string;
  food_preference: string;
  progress: string;
  medical_conditions: string;
  last_status: string | null;
  profile_url: string;
}

// Transformed Plan Data Types
interface PlanDetails {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  issignature: boolean;
  perfectFor: string[];
  whatYouGet: string[];
  whatsNotIncluded?: string[];
  programDetails?: string[];
  featuresTable?: {
    headers: string[];
    rows: {
      feature: string;
      values: string[];
    }[];
  };
  pricingOptions: {
    duration: string;
    originalPrice?: string;
    price: string;
    standardDetailsId: number;
    productId: string;
  }[];
  additionalInfo?: string;
}

// Payment Success Data Interface
interface PaymentSuccessData {
  planType: string;
  duration: number;
  paymentDetails: {
    orderId: string;
    paymentId: string;
    amount: number;
    transactionDate: string;
    apiPaymentId?: string;
    signatureId?: string;
    invoiceNumber?: string;
    userPaymentId?: number;
  };
}

// View types for navigation
type ViewType =
  | "plans"
  | "planDetails"
  | "expertForm"
  | "login"
  | "otpVerification"
  | "userRegistration"
  | "recommendedPlanDetails"
  | "paymentSuccess"
  | "signaturePlanSuccess";

  // Auto-redirect component: instantly navigates away, no flash of error UI
const AutoRedirect: React.FC<{ onRedirect: () => void }> = ({ onRedirect }) => {
  useEffect(() => {
    onRedirect();
  }, []);
  return null;
};

// Static configuration for all four plans
const staticPlansConfig = {
  lite: {
    id: "lite",
    title: "Triva Lite",
    defaultSubtitle: "Basic nutrition tracking",
    icon: <LiteBgImageIcon className="w-[40px] h-[40px]" />,
    bgColor: "bg-[#FDF7FC]",
    borderColor: "border-[#E47DCB]",
  },
  spark: {
    id: "spark",
    title: "Triva Spark",
    defaultSubtitle: "Enhanced features for better results",
    icon: <SparkBgImageIcon className="w-[40px] h-[40px]" />,
    bgColor: "bg-[#FCF8F0]",
    borderColor: "border-[#C5A344]",
  },
  ignite: {
    id: "ignite",
    title: "Triva Ignite",
    defaultSubtitle: "Advanced meal planning and tracking",
    icon: <IgniteBgImageIcon className="w-[40px] h-[40px]" />,
    bgColor: "bg-[#F5FEE1]",
    borderColor: "border-[#86BC0B]",
  },
  thrive: {
    id: "thrive",
    title: "Triva Thrive",
    defaultSubtitle: "Complete wellness transformation",
    icon: <ThriveBgImageIcon className="w-[40px] h-[40px]" />,
    bgColor: "bg-[#F1EDFD]",
    borderColor: "border-[#B7AAF5]",
  },
};

const PlanCard: React.FC<PlanCardProps> = ({
  icon,
  title,
  description,
  bgColor,
  borderColor = "border-gray-200",
  issignature = false,
  onSelect,
  price,
  originalPrice,
}) => (
  <div
    className={`relative ${bgColor} ${borderColor} border-[0.5px] rounded-2xl px-6 py-4 cursor-pointer transition-all duration-200 hover:shadow-lg mb-4`}
    onClick={onSelect}
  >
    {issignature && (
      <div className="absolute -top-3 left-6">
        <GradientBorderPremiumTag>
          <Star className="w-4 h-4 mr-1 text-white" />
          Premium
        </GradientBorderPremiumTag>
      </div>
    )}

    <div className="flex items-center justify-between">
      <div className="flex items-center flex-1">
        <div className="mr-4 flex-shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold font-inter text-[20px] text-[#202020]">
            {title}
          </h3>
          <p className="text-[12px] font-inter text-[#646464] leading-tight max-w-sm">
            {description}
          </p>
        </div>
      </div>

      {/* Pricing and Plus Icon Container */}
      <div className="flex items-center gap-3 ml-4 flex-shrink-0">
        {/* Pricing Tag - Always show for non-signature plans */}
        {!issignature && price && (
          <div className="flex flex-col items-end leading-none">
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-inter text-[#646464] italic">
                Starts at :
              </span>

              {/* Only show strikethrough if there's an actual discount */}
              {originalPrice && originalPrice !== price && (
                <span className="text-[12px] font-inter text-[#646464] line-through">
                  ₹{originalPrice}
                </span>
              )}

              <span className="text-[16px] font-semibold font-inter text-[#202020]">
                ₹{price}
              </span>
            </div>

            <span className="text-[8px] font-inter text-[#646464] leading-none">
              /month
            </span>
          </div>
        )}

        <Plus className="w-6 h-6 text-[#202020]" />
      </div>
    </div>
  </div>
);

// Features Table Component
interface FeaturesTableProps {
  featuresTable: {
    headers: string[];
    rows: {
      feature: string;
      values: string[];
    }[];
  };
}

const FeaturesTable: React.FC<FeaturesTableProps> = ({ featuresTable }) => {
  return (
    <div className="mb-6">
      <div className="rounded-xl border-[0.5px] border-[#6DD0CF] overflow-hidden">
        {/* Header (with background) */}
        <div
          className="grid bg-[#F3FBFB]"
          style={{ gridTemplateColumns: "50% repeat(3, 1fr)" }}
        >
          <div className="px-3 py-2 text-left">
            <span className="text-[12px] 3xl:text-[14px] font-semibold text-[#1F1F1F]">
              Feature
            </span>
          </div>

          {featuresTable.headers.map((header, index) => (
            <div key={index} className="px-3 py-2 text-center">
              <span className="text-[12px] 3xl:text-[14px] font-semibold text-[#1F1F1F]">
                {header}
              </span>
            </div>
          ))}
        </div>

        {/* Rows (no background) */}
        {featuresTable.rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="grid border-t-[0.5px] border-[#E0E0E0] bg-white"
            style={{ gridTemplateColumns: "50% repeat(3, 1fr)" }}
          >
            <div className="px-3 py-2 text-left">
              <span className="text-[12px] 3xl:text-[14px] text-[#646464]">
                {row.feature}
              </span>
            </div>

            {row.values.map((value, colIndex) => (
              <div key={colIndex} className="px-3 py-2 text-center">
                <span className="text-[12px] 3xl:text-[14px] text-[#646464]">
                  {value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

interface PlanDetailsProps {
  planData: PlanDetails;
  userId: number | null;
  onBack: () => void;
  onConnectWithExperts: () => void;
  onBuyNow: () => void;
  setSelectedPricingOptionFromDetails: (option: string | null) => void;
}

const PlanDetailsScreen: React.FC<PlanDetailsProps> = ({
  planData,
  userId,
  onBack,
  onConnectWithExperts,
  onBuyNow,
  setSelectedPricingOptionFromDetails,
}) => {
  const [selectedPricingOption, setSelectedPricingOption] = useState<
    string | null
  >(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const getSelectedPlanPrice = (): number => {
    if (!selectedPricingOption) return 0;
    const option = planData.pricingOptions.find(
      (option) => option.duration === selectedPricingOption,
    );
    return option ? parseFloat(option.price) : 0;
  };

  const handlePayment = () => {
    if (!selectedPricingOption) return;

    const selectedOption = planData.pricingOptions.find(
      (option) => option.duration === selectedPricingOption,
    );

    const basePrice = getSelectedPlanPrice();

    setSelectedPricingOptionFromDetails(selectedPricingOption);

    const paymentData = {
      userId: userId,
      planId: selectedOption?.standardDetailsId,
      planTitle: planData.title,
      duration: selectedPricingOption,
      basePrice: basePrice,
      totalPrice: basePrice,
      planType: "regular",
      productId: selectedOption?.productId,
    };

    console.log("Payment initiated with data:", paymentData);
    onBuyNow();
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case "lite":
        return <LiteBgImageIcon className="w-[40px] h-[40px]" />;
      case "spark":
        return <SparkBgImageIcon className="w-[40px] h-[40px]" />;
      case "ignite":
        return <IgniteBgImageIcon className="w-[40px] h-[40px]" />;
      case "thrive":
        return <ThriveBgImageIcon className="w-[40px] h-[40px]" />;
      case "signature":
        return <SignatureBgImageIcon className="w-[40px] h-[40px]" />;
      default:
        return <LiteBgImageIcon className="w-[40px] h-[40px]" />;
    }
  };

  const getPlanColors = (planId: string) => {
    switch (planId) {
      case "lite":
        return { bg: "bg-[#FDF7FC]", border: "border-[#E47DCB]" };
      case "spark":
        return { bg: "bg-[#FCF8F0]", border: "border-[#C5A344]" };
      case "ignite":
        return { bg: "bg-[#F5FEE1]", border: "border-[#86BC0B]" };
      case "thrive":
        return { bg: "bg-[#F1EDFD]", border: "border-[#B7AAF5]" };
      case "signature":
        return { bg: "bg-[#F9FEFE]", border: "border-[#6DD0CF]" };
      default:
        return { bg: "bg-white", border: "border-gray-200" };
    }
  };

  const colors = getPlanColors(planData.id);

  const handleAdditionalInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open("https://fit4lifeindia.com/why-choose-us/", "_blank");
  };

  const getFeaturesToDisplay = () => {
    if (showAllFeatures || planData.whatYouGet.length <= 3) {
      return planData.whatYouGet;
    }
    return planData.whatYouGet.slice(0, 3);
  };

  // Get the lowest price for "Starts at" display
  const getLowestPrice = () => {
    if (!planData.pricingOptions || planData.pricingOptions.length === 0) {
      return null;
    }

    const lowestPriceOption = planData.pricingOptions.reduce(
      (lowest, current) => {
        const currentPrice = parseFloat(current.price);
        const lowestPrice = parseFloat(lowest.price);
        return currentPrice < lowestPrice ? current : lowest;
      },
    );

    return lowestPriceOption;
  };

  const lowestPriceOption = getLowestPrice();

  return (
    <>
      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 2px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 0;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 0;
          border: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
        
        /* Completely hide all scrollbar buttons and corners */
        .custom-scrollbar::-webkit-scrollbar-button:single-button {
          display: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-button:double-button {
          display: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-button {
          display: none;
          height: 0;
          width: 0;
        }
        
        .custom-scrollbar::-webkit-scrollbar-corner {
          display: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track-piece {
          background: transparent;
        }
        
        /* Firefox scrollbar styling */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #D1D5DB transparent;
        }
      `}</style>
      <div
        className={`flex-1 ${colors.border} border-[0.5px] rounded-2xl flex flex-col max-h-[450px] xl:max-h-[550px] 2xl:max-h-[600px] 3xl:max-h-[700px] 4xl:max-h-[800px]`}
      >
        {/* Plan Card Header - Fixed Position */}
        <div
          className={`relative ${colors.bg} ${colors.border} border-b-[0.5px] rounded-t-2xl px-6 py-5 flex-shrink-0`}
        >
          {planData.issignature && (
            <div className="absolute -top-3 left-6">
              <GradientBorderPremiumTag>
                <Star className="w-4 h-4 mr-1 text-white" />
                Premium
              </GradientBorderPremiumTag>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <div className="mr-4 flex-shrink-0">
                {getPlanIcon(planData.id)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold font-inter text-[20px] text-[#202020]">
                  {planData.title}
                </h3>
                <p className="text-[12px] font-inter text-[#646464] leading-relaxed">
                  {planData.subtitle}
                </p>
              </div>
            </div>

            {/* Pricing Tag in Header - Always show for non-signature plans */}
            <div className="flex items-center gap-3">
              {!planData.issignature && lowestPriceOption && (
                <div className="flex flex-col items-end mr-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-inter text-[#646464] mb-0.5">
                      Starts at :
                    </span>
                    {/* Only show Original Price strikethrough if discount exists */}
                    {lowestPriceOption.originalPrice &&
                      lowestPriceOption.originalPrice !==
                        lowestPriceOption.price && (
                        <span className="text-[12px] font-inter text-[#646464] line-through">
                          ₹{lowestPriceOption.originalPrice}
                        </span>
                      )}
                    {/* Discounted/Current Price */}
                    <span className="text-[16px] font-semibold font-inter text-[#202020]">
                      ₹{lowestPriceOption.price}
                    </span>
                  </div>
                  <span className="text-[8px] font-inter text-[#646464]">
                    /month
                  </span>
                </div>
              )}

              <button onClick={onBack}>
                <X className="w-4 h-4 text-[#202020]" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area with Custom Scrollbar */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="px-6 pb-4">
            {/* Additional Info for signature */}
            {planData.additionalInfo && (
              <div className="mb-4 pt-6">
                <button
                  onClick={handleAdditionalInfoClick}
                  className="flex items-center text-[#003E3E] font-inter font-medium text-[12px] 3xl:text-[14px] hover:underline transition-all duration-200 cursor-pointer"
                >
                  {planData.additionalInfo}
                  <TrendingUp className="w-4 h-4 ml-2" />
                </button>
              </div>
            )}

            {/* Description */}
            <div className="mb-6 pt-6">
              <p className="text-[#202020] text-[12px] 3xl:text-[14px] font-inter font-medium">
                {planData.description}
              </p>
            </div>

            {/* Perfect For */}
            <div className="mb-6">
              <div className="flex">
                <div className="shrink-0 mr-2">
                  <span className="text-[14px] 3xl:text-[16px] font-inter font-semibold text-[#202020]">
                    Perfect for:
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {planData.perfectFor.map((item, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-[#F1FBFB] text-[#003E3E] text-[12px] 3xl:text-[14px] rounded-[10px] border-[0.5px] border-[#6DD0CF]"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Features Table */}
            {planData.featuresTable && (
              <FeaturesTable featuresTable={planData.featuresTable} />
            )}

            {/* What you get - Updated with Read More functionality */}
            <div className="mb-6">
              <div className="text-[14px] 3xl:text-[16px] font-inter font-semibold text-[#202020] mb-2">
                What you get:
              </div>
              <div className="space-y-2">
                {getFeaturesToDisplay().map((item, index) => (
                  <div key={index} className="flex items-start">
                    <Check className="w-3 h-3 text-[#003E3E] mr-2 mt-1.5 flex-shrink-0" />
                    <span className="text-[#202020] font-inter text-[12px] 3xl:text-[14px] leading-relaxed mb-1">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              {/* Read More / Show Less Link */}
              {planData.whatYouGet.length > 3 && (
                <button
                  onClick={() => setShowAllFeatures(!showAllFeatures)}
                  className="mt-3 text-[#00A9A9] font-inter font-medium text-[12px] 3xl:text-[14px] transition-all duration-200 cursor-pointer flex items-center"
                >
                  {showAllFeatures ? (
                    <>
                      Show less
                      <ArrowUp className="w-4 h-4 3xl:w-5 3xl:h-5 4xl:w-6 4xl:h-6 ml-1" />
                    </>
                  ) : (
                    <>
                      Read more
                      <ArrowDown className="w-4 h-4 3xl:w-5 3xl:h-5 4xl:w-6 4xl:h-6 ml-1" />
                    </>
                  )}
                </button>
              )}

              {planData.programDetails && showAllFeatures && (
                <div className="mt-4 ml-4">
                  {planData.programDetails.map((detail, index) => (
                    <div
                      key={index}
                      className="text-[#202020] font-inter text-[12px] 3xl:text-[14px] leading-relaxed mb-1"
                    >
                      {detail}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* What's not included */}
            {planData.whatsNotIncluded && planData.whatsNotIncluded.length > 0 && (
            <div className="mb-6">
              <div className="text-[14px] 3xl:text-[16px] font-inter font-semibold text-[#202020] mb-2">
                What's not included:
              </div>
              <div className="space-y-2">
                {planData.whatsNotIncluded.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <AlertCircle className="w-3 h-3 text-[#FF3B30] mr-2 mt-1 flex-shrink-0" />
                    <span className="text-[#202020] font-inter text-[12px] 3xl:text-[14px] leading-relaxed mb-1">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Fixed Bottom Section - Buy Plan for non-signature plans */}
        {!planData.issignature && planData.pricingOptions.length > 0 && (
          <div className="flex-shrink-0 px-6 py-4 bg-white border-t-[0.5px] border-[#E5E7EB] rounded-b-2xl">
            <div className="text-[14px] 3xl:text-[16px] font-inter font-semibold text-[#202020] mb-3">
              Buy plan
            </div>

            <div className="flex gap-2 2xl:gap-4 mb-4">
              {planData.pricingOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedPricingOption(option.duration)}
                  className={`flex-1 flex items-center justify-between px-3 py-2 rounded-xl border-[0.5px] ${
                    selectedPricingOption === option.duration
                      ? "border-[#0DB5B5] bg-[#F1FBFB] text-[#0DB5B5]"
                      : "border-[#8D8D8D] bg-white text-[#646464]"
                  } hover:border-[#0DB5B5] transition-colors`}
                >
                  <div className="text-[10px] 3xl:text-[12px] font-medium">
                    {option.duration}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* ✅ Show strikethrough originalPrice if discount exists */}
                    {option.originalPrice &&
                      option.originalPrice !== option.price && (
                        <div className="flex items-center">
                          <span className="text-[10px] 2xl:text-[12px] 3xl:text-[14px] font-inter text-[#646464] line-through">
                            ₹{option.originalPrice}
                          </span>
                        </div>
                      )}

                    {/* Current/Discounted Price */}
                    <div className="flex items-center">
                      <span className="text-[14px] font-semibold text-[#007E7F] mr-1">
                        ₹
                      </span>
                      <div className="font-semibold font-inter text-[14px] 2xl:text-[16px] 3xl:text-[18px] text-[#0DB5B5]">
                        {option.price}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex items-start justify-between">
              <p className="text-[10px] 3xl:text-xs text-[#646464] mb-0 flex items-center">
                <span className="inline-flex items-center mr-1">
                  <span className="w-4 h-4 rounded-full border border-[#646464] flex items-center justify-center mr-1">
                    <span className="text-[10px]">i</span>
                  </span>
                </span>
                No auto-renewal. Top-up anytime.
              </p>

              <button
                className={`w-[174px] px-[20px] py-3 rounded-lg font-medium transition-colors ${
                  selectedPricingOption
                    ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                    : "bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed"
                }`}
                disabled={!selectedPricingOption}
                onClick={handlePayment}
              >
                Buy now
              </button>
            </div>
          </div>
        )}

        {/* Fixed Bottom Section - Connect with experts button for signature */}
        {planData.issignature && (
          <div className="flex-shrink-0 px-6 py-4 bg-white border-t-[0.5px] border-[#E5E7EB] rounded-b-2xl flex justify-end">
            <button
              onClick={onConnectWithExperts}
              className="w-[200px] px-[20px] bg-[#0DB5B5] text-white py-3 rounded-lg font-medium hover:bg-[#0A9999] transition-colors"
            >
              Connect with experts
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const TrivaPlansDesktop: React.FC<TrivaPlansDesktopProps> = ({ userId, directPurchasePlanId, deeplinkPlan }) => {
  const [currentView, setCurrentView] = useState<ViewType>(
    directPurchasePlanId ? "login" : "plans"
  );
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedPlanDetails, setSelectedPlanDetails] =
    useState<PlanDetails | null>(null);
  const [allPlansData, setAllPlansData] = useState<PlanDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [
    selectedPricingOptionFromDetails,
    setSelectedPricingOptionFromDetails,
  ] = useState<string | null>(null);

  // State for login flow
  const [currentLoginData, setCurrentLoginData] = useState<LoginData | null>(
    null,
  );
  const [verifiedUserId, setVerifiedUserId] = useState<number | null>(null);
  const [verifiedAccessToken, setVerifiedAccessToken] = useState<string>("");
  const [isProcessingGoogleLogin, setIsProcessingGoogleLogin] = useState(false);
  const [googleUserEmail, setGoogleUserEmail] = useState<string | null>(null);

  // State for payment success data
  const [paymentSuccessData, setPaymentSuccessData] =
    useState<PaymentSuccessData | null>(null);

  // Static signature plan data
  const signaturePlan: PlanDetails = {
    id: SIGNATURE_PLAN_ID,
    title: SIGNATURE_PLAN_TITLE,
    subtitle: SIGNATURE_PLAN_SUBTITLE,
    description: SIGNATURE_PLAN_DESCRIPTION,
    issignature: true,
    perfectFor: SIGNATURE_PLAN_PERFECT_FOR,
    whatYouGet: SIGNATURE_PLAN_WHAT_YOU_GET,
    pricingOptions: [],
    additionalInfo: SIGNATURE_PLAN_ADDITIONAL_INFO,
  };

  // URL management
  const updateURL = (view: ViewType, planId?: string) => {
    let newURL = window.location.origin + window.location.pathname;

    switch (view) {
      case "plans":
        newURL += "#plans";
        break;
      case "planDetails":
        newURL += `#plan-details/${planId}`;
        break;
      case "expertForm":
        newURL += "#expert-connection";
        break;
      case "login":
        newURL += "#login";
        break;
      case "otpVerification":
        newURL += "#otp-verification";
        break;
      case "userRegistration":
        newURL += "#user-registration";
        break;
      case "recommendedPlanDetails":
        newURL += "#recommended-plan-details";
        break;
      case "paymentSuccess":
        newURL += "#payment-success";
        break;
      case "signaturePlanSuccess":
        newURL += "#signature-plan-success";
        break;
    }

    window.history.pushState({ view, planId }, "", newURL);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const { view, planId } = event.state || {};

      if (view) {
        setCurrentView(view);
        if (planId) {
          handlePlanSelect(planId);
        } else {
          setSelectedPlan(null);
          setSelectedPlanDetails(null);
        }
      } else {
        // Handle direct URL access
        const hash = window.location.hash;
        if (hash.includes("#plan-details/")) {
          const planId = hash.split("/")[1];
          handlePlanSelect(planId);
        } else if (hash === "#expert-connection") {
          setCurrentView("expertForm");
        } else if (hash === "#login") {
          setCurrentView("login");
        } else if (hash === "#otp-verification") {
          setCurrentView("otpVerification");
        } else if (hash === "#user-registration") {
          setCurrentView("userRegistration");
        } else if (hash === "#recommended-plan-details") {
          setCurrentView("recommendedPlanDetails");
        } else if (hash === "#payment-success") {
          setCurrentView("paymentSuccess");
        } else if (hash === "#signature-plan-success") {
          setCurrentView("signaturePlanSuccess");
        }
      }
    };

    window.addEventListener("popstate", handlePopState);

    // Handle initial page load
    const hash = window.location.hash;
    if (hash.includes("#plan-details/")) {
      const planId = hash.split("/")[1];
      handlePlanSelect(planId);
    } else if (hash === "#expert-connection") {
      setCurrentView("expertForm");
    } else if (hash === "#login") {
      setCurrentView("login");
    } else if (hash === "#otp-verification") {
      setCurrentView("otpVerification");
    } else if (hash === "#user-registration") {
      setCurrentView("userRegistration");
    } else if (hash === "#recommended-plan-details") {
      setCurrentView("recommendedPlanDetails");
    } else if (hash === "#payment-success") {
      setCurrentView("paymentSuccess");
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    fetchAndPrepareAllPlans();
  }, []);

  // Handle direct purchase URL (e.g. /purchase/17)
  // Sets plan details in the background while the login form is already shown
  useEffect(() => {
    if (!directPurchasePlanId || allPlansData.length === 0) return;

    for (const plan of allPlansData) {
      const matchingOption = plan.pricingOptions.find(
        // Use Number() to handle cases where the API returns the ID as a string
        (opt) => Number(opt.standardDetailsId) === Number(directPurchasePlanId)
      );
      if (matchingOption) {
        setSelectedPlanDetails(plan);
        setSelectedPlan(plan.id);
        setSelectedPricingOptionFromDetails(matchingOption.duration);
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directPurchasePlanId, allPlansData]);

  // Handle deeplink: navigate directly to plan details (auth already set by DeeplinkHandler)
  useEffect(() => {
    if (!deeplinkPlan) return;

    if (deeplinkPlan === 'signature') {
      setSelectedPlanDetails(signaturePlan);
      setSelectedPlan('signature');
      setCurrentView('planDetails');
      return;
    }

    if (allPlansData.length === 0) return;
    const validPlans = ['lite', 'spark', 'ignite', 'thrive'];
    if (!validPlans.includes(deeplinkPlan)) return;

    const planData = allPlansData.find((p) => p.id === deeplinkPlan);
    if (!planData) return;

    setSelectedPlanDetails(planData);
    setSelectedPlan(deeplinkPlan);
    setCurrentView('planDetails');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deeplinkPlan, allPlansData]);

  const fetchAndPrepareAllPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Fetching plans data from:", API_URLS.Get_STANDARD_PLANS);

      const response = await fetch(API_URLS.Get_STANDARD_PLANS);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      console.log("Raw API response:", data);

      if (data.success !== "1") {
        throw new Error(data.message || "Failed to fetch plans");
      }

      console.log("Plans data from API:", data.plans);

      // Create all four plans, ensuring we always have them
      const preparedPlans = createAllFourPlans(data.plans);

      console.log("Prepared all plans:", preparedPlans);
      setAllPlansData(preparedPlans);
    } catch (err) {
      console.error("Error fetching plans data:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred",
      );

      // Create fallback plans with default data if API fails
      const fallbackPlans = createFallbackPlans();
      setAllPlansData(fallbackPlans);
    } finally {
      setLoading(false);
    }
  };

  const createAllFourPlans = (apiPlans: ApiPlanData[]): PlanDetails[] => {
    const allPlans: PlanDetails[] = [];

    // Iterate through our static config to ensure all four plans are created
    Object.values(staticPlansConfig).forEach((staticConfig) => {
      // Try to find matching API data
      const matchingApiPlan = findApiPlanByType(apiPlans, staticConfig.id);

      if (matchingApiPlan) {
        // Use API data
        try {
          const transformedPlan = transformApiDataToPlanData(
            matchingApiPlan,
            staticConfig,
          );
          allPlans.push(transformedPlan);
          console.log(`Created plan from API data: ${transformedPlan.title}`);
        } catch (error) {
          console.error(
            `Error transforming API plan for ${staticConfig.id}:`,
            error,
          );
          // Fall back to default plan
          const fallbackPlan = createDefaultPlan(staticConfig);
          allPlans.push(fallbackPlan);
          console.log(`Created fallback plan: ${fallbackPlan.title}`);
        }
      } else {
        // Create default plan when no API data available
        const defaultPlan = createDefaultPlan(staticConfig);
        allPlans.push(defaultPlan);
        console.log(`Created default plan (no API data): ${defaultPlan.title}`);
      }
    });

    return allPlans;
  };

  const findApiPlanByType = (
    apiPlans: ApiPlanData[],
    planType: string,
  ): ApiPlanData | null => {
    const planMappings = {
      lite: "Triva Lite",
      spark: "Triva Spark",
      ignite: "Triva Ignite",
      thrive: "Triva Thrive",
    };

    const searchTerm = planMappings[planType as keyof typeof planMappings];
    if (!searchTerm) return null;

    return (
      apiPlans.find((plan) =>
        plan.subscription_plan_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ) || null
    );
  };

  const createDefaultPlan = (
    staticConfig: typeof staticPlansConfig.lite,
  ): PlanDetails => {
    return {
      id: staticConfig.id,
      title: staticConfig.title,
      subtitle: staticConfig.defaultSubtitle,
      description: `${staticConfig.title} - ${staticConfig.defaultSubtitle}`,
      issignature: false,
      perfectFor: [
        "Health conscious individuals",
        "Nutrition tracking",
        "Wellness journey",
      ],
      whatYouGet: [
        "Basic nutrition tracking",
        "Meal planning features",
        "Progress monitoring",
        "Health insights",
      ],
      whatsNotIncluded: ["Premium features available in higher plans"],
      pricingOptions: [
        {
          duration: "For 1 month @",
          price: "99",
          standardDetailsId: 0,
          productId: "",
        },
      ],
    };
  };

  const createFallbackPlans = (): PlanDetails[] => {
    return Object.values(staticPlansConfig).map((config) =>
      createDefaultPlan(config),
    );
  };

  // Parse features table from API data
  const parseFeaturesTable = (
    apiPlan: ApiPlanData,
  ):
    | {
        headers: string[];
        rows: {
          feature: string;
          values: string[];
        }[];
      }
    | undefined => {
    // Check if any duration has features data
    const featuresData = apiPlan.duration_and_price_and_perks.find(
      (option) =>
        option.std_subscription_features &&
        option.std_subscription_features.trim() !== "",
    );

    if (!featuresData || !featuresData.std_subscription_features) {
      return undefined;
    }

    const features = featuresData.std_subscription_features;

    // Parse the features string
    // Expected format: "Feature1:val1,val2,val3\n<br>Feature2:val1,val2,val3"
    const rows = features
      .replace(/<br>/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const [feature, valuesStr] = line.split(":");
        const values = valuesStr
          ? valuesStr.split(",").map((v) => v.trim())
          : [];
        return {
          feature: feature.trim(),
          values,
        };
      })
      .filter((row) => row.values.length > 0);

    if (rows.length === 0) {
      return undefined;
    }

    // Determine headers based on duration options (1m, 3m, 6m)
    const durations = apiPlan.duration_and_price_and_perks
      .map((option) => option.duration_period)
      .sort((a, b) => {
        const durationOrder = { "1 Month": 1, "3 Months": 2, "6 Months": 3 };
        return (
          (durationOrder[a as keyof typeof durationOrder] || 999) -
          (durationOrder[b as keyof typeof durationOrder] || 999)
        );
      });

    const headers = durations.map((duration) => {
      if (duration === "1 Month") return "1m";
      if (duration === "3 Months") return "3m";
      if (duration === "6 Months") return "6m";
      return duration;
    });

    return {
      headers,
      rows,
    };
  };

  const transformApiDataToPlanData = (
    apiPlan: ApiPlanData,
    staticConfig: typeof staticPlansConfig.lite,
  ): PlanDetails => {
    try {
      console.log("Processing plan:", apiPlan.subscription_plan_name);

      // Parse title and subtitle from plan name
      const planNameMatch = apiPlan.subscription_plan_name.match(
        /^([^(]+)(?:\s*\(([^)]+)\))?/,
      );
      const title = planNameMatch
        ? planNameMatch[1].trim()
        : staticConfig.title;
      const subtitle =
        planNameMatch && planNameMatch[2]
          ? planNameMatch[2].trim()
          : staticConfig.defaultSubtitle;

      // Get first option for basic info (with safety check)
      const firstOption = apiPlan.duration_and_price_and_perks?.[0];

      // ✅ Use discount price for calculations, original price for strikethrough
      const pricingOptions = (apiPlan.duration_and_price_and_perks || [])
        .sort((a, b) => {
          const durationOrder = { "1 Month": 1, "3 Months": 2, "6 Months": 3 };
          return (
            (durationOrder[a.duration_period as keyof typeof durationOrder] ||
              999) -
            (durationOrder[b.duration_period as keyof typeof durationOrder] ||
              999)
          );
        })
        .map((option) => ({
          duration: formatDuration(option.duration_period || ""),
          price: option.std_subscription_discount_price || "0",
          originalPrice:
            option.std_subscription_discount_price !==
            option.std_subscription_price
              ? option.std_subscription_price
              : undefined,
          standardDetailsId: option.standard_details_id || 0,
          productId: option.product_id || "",
        }));

      const whatYouGet = parsePerksToArray(apiPlan.std_subscription_perks);
      const perfectFor = parsePerfectFor(
        firstOption?.std_subscription_perfect_for || "",
      );

      // Parse features table
      const featuresTable = parseFeaturesTable(apiPlan);

      const result = {
        id: staticConfig.id,
        title,
        subtitle,
        description: firstOption?.std_subscription_description || subtitle,
        issignature: false,
        perfectFor,
        whatYouGet,
        whatsNotIncluded: firstOption?.std_subscription_not_included
          ? [firstOption.std_subscription_not_included]
          : [],
        featuresTable,
        pricingOptions,
      };

      console.log("Successfully transformed plan:", result);
      return result;
    } catch (error) {
      console.error("Error in transformApiDataToPlanData:", error);
      throw error;
    }
  };

  const formatDuration = (duration: string): string => {
    const mapping = {
      "1 Month": "For 1 month @",
      "3 Months": "For 3 month @",
      "6 Months": "For 6 month @",
      "1 time": "For 1 time @",
    };
    return mapping[duration as keyof typeof mapping] || `For ${duration} @`;
  };

  const parsePerksToArray = (perks: any): string[] => {
    if (!perks) return [];

    const perksString = typeof perks === "string" ? perks : String(perks);

    if (!perksString.trim()) return [];

    let cleanedPerks = perksString
      .replace(/<[^>]*>/g, "")
      .replace(/\\r\\n|\\r|\\n|\r\n|\r|\n/g, "|BREAK|")
      .trim();

    if (!cleanedPerks.includes("|BREAK|")) {
      return cleanedPerks ? [cleanedPerks] : [];
    }

    return cleanedPerks
      .split("|BREAK|")
      .map((perk) => perk.trim())
      .filter((perk) => perk && perk.length > 0)
      .map((perk) => perk.replace(/,$/, ""));
  };

  const parsePerfectFor = (perfectForString: any): string[] => {
    if (!perfectForString) return [];

    const perfectForStr =
      typeof perfectForString === "string"
        ? perfectForString
        : String(perfectForString);

    if (!perfectForStr.trim()) return [];

    return perfectForStr
      .replace(/"/g, "")
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  // Smart navigation function that considers authentication state
  const navigateToDestination = (planData: PlanDetails) => {
    console.log("=== SMART NAVIGATION START ===");
    console.log("Plan:", planData.title, planData.id);
    console.log("Auth Debug Info:", AuthStorage.getDebugInfo());

    if (AuthStorage.isAuthenticated()) {
      const userData = AuthStorage.getUserData();
      console.log("User authenticated, userData:", userData);

      // User is authenticated, go directly to destination
      if (planData.issignature) {
        console.log("Navigating to expert form (authenticated)");
        setCurrentView("expertForm");
        updateURL("expertForm");
      } else {
        console.log("Navigating to recommended plan details (authenticated)");
        setCurrentView("recommendedPlanDetails");
        updateURL("recommendedPlanDetails");
      }
    } else {
      console.log("User not authenticated, showing login");
      setCurrentView("login");
      updateURL("login");
    }

    console.log("=== SMART NAVIGATION END ===");
  };

  const handlePlanSelect = (planId: string) => {
    console.log(`Plan selection requested for: ${planId}`);

    let planData: PlanDetails | null = null;

    if (planId === "signature") {
      console.log("Selecting signature plan");
      planData = signaturePlan;
    } else {
      // Find the plan from all plans data
      planData = allPlansData.find((plan) => plan.id === planId) || null;

      if (!planData) {
        console.error(`Plan with ID "${planId}" not found`);
        return;
      }

      console.log(`Found matching plan: ${planData.id} - ${planData.title}`);
    }

    if (planData) {
      setSelectedPlanDetails(planData);
      setSelectedPlan(planId);
      setCurrentView("planDetails");
      updateURL("planDetails", planId);
      console.log(`Successfully selected plan: ${planId} (${planData.title})`);
    } else {
      console.error(`Failed to find plan data for ID: ${planId}`);
    }
  };

  const handleBackToPlans = () => {
    console.log("Navigating back to plans list");
    setSelectedPlanDetails(null);
    setSelectedPlan(null);
    setCurrentView("plans");
    updateURL("plans");
  };

  const handleConnectWithExperts = () => {
    console.log("Connect with experts requested");
    if (selectedPlanDetails) {
      navigateToDestination(selectedPlanDetails);
    }
  };

  const handleBuyNow = () => {
    console.log("Buy now requested");
    if (selectedPlanDetails) {
      navigateToDestination(selectedPlanDetails);
    }
  };

  const handleBackFromExpertForm = () => {
    setCurrentView("planDetails");
    if (selectedPlan) {
      updateURL("planDetails", selectedPlan);
    }
  };

  const handleBackFromLogin = () => {
    if (selectedPlanDetails) {
      setCurrentView("planDetails");
      updateURL("planDetails", selectedPlan || undefined);
    } else {
      setCurrentView("plans");
      updateURL("plans");
    }
  };
  // Ensures the plan matching directPurchasePlanId is selected.
  // Called by auth handlers at the moment of navigation so selectedPlanDetails
  // is always set (batched together with setCurrentView) before the next render.
  const resolveDirectPlan = (): PlanDetails | null => {
    if (selectedPlanDetails) return selectedPlanDetails;
    if (!directPurchasePlanId || allPlansData.length === 0) return null;

    for (const plan of allPlansData) {
      const matchingOption = plan.pricingOptions.find(
        (opt) => Number(opt.standardDetailsId) === Number(directPurchasePlanId)
      );
      if (matchingOption) {
        setSelectedPlanDetails(plan);
        setSelectedPlan(plan.id);
        setSelectedPricingOptionFromDetails(matchingOption.duration);
        return plan;
      }
    }
    return null;
  };

  const handleGoogleLoginSuccess = (
    userData: any,
    needsRegistration: boolean,
  ) => {
    console.log("=== Google Login Success Handler (Updated) ===");
    console.log("User Data:", userData);
    console.log("Needs Registration:", needsRegistration);
    console.log("Auth Debug Info:", AuthStorage.getDebugInfo());

    // IMPORTANT: Clear processing state immediately when navigation happens
    setIsProcessingGoogleLogin(false);

    // Store Google email as fallback for the payment screen
    if (userData?.email) {
      setGoogleUserEmail(userData.email);
    }

    if (needsRegistration) {
      console.log("Google user needs registration, showing registration form");
      setCurrentView("userRegistration");
      updateURL("userRegistration");
    } else {
      console.log(
        "Google user complete, navigating directly to destination (SKIPPING OTP)",
      );

      const resolvedPlan = resolveDirectPlan();
      if ((resolvedPlan ?? selectedPlanDetails)?.issignature) {
        console.log("Navigating to expert form for signature plan");
        setCurrentView("expertForm");
        updateURL("expertForm");
      } else {
        console.log("Navigating to recommended plan details for regular plan");
        setCurrentView("recommendedPlanDetails");
        updateURL("recommendedPlanDetails");
      }
    }
  };

  // Updated login success handler
  const handleLoginSuccess = (userData?: any) => {
    console.log("Traditional login successful, user data:", userData);

    // IMPORTANT: Clear Google processing state
    setIsProcessingGoogleLogin(false);

    // For traditional login, navigation is handled by OTP verification
    // This callback is mainly for cleanup purposes
  };
  const handleGoogleLoginError = () => {
    console.log("Google login failed or was cancelled");

    // IMPORTANT: Clear processing state when Google login fails
    setIsProcessingGoogleLogin(false);
  };

  // New handler for OTP requirement
  const handleOTPRequired = (loginData: LoginData) => {
    console.log("=== OTP Required Handler ===");
    console.log("Login data:", loginData);
    console.log("Is Google Authenticated:", loginData.isGoogleAuthenticated);

    // Store tokens immediately when login API succeeds
    AuthStorage.setAuthData(
      loginData.access_token,
      loginData.refresh_token,
      loginData.user_id,
    );

    // UPDATED: Check if this is a Google authenticated user
    if (loginData.isGoogleAuthenticated) {
      console.log(
        "Google authenticated user - This should not require OTP, redirecting...",
      );

      // This shouldn't happen with the updated flow, but as a safety net:
      // Navigate directly based on user completeness
      const userData = AuthStorage.getUserData();
      const hasCompleteName =
        userData?.user_fname && userData.user_fname.trim() !== "";

      const resolvedPlan = resolveDirectPlan();
      if (!hasCompleteName) {
        setCurrentView("userRegistration");
        updateURL("userRegistration");
      } else if ((resolvedPlan ?? selectedPlanDetails)?.issignature) {
        setCurrentView("expertForm");
        updateURL("expertForm");
      } else {
        setCurrentView("recommendedPlanDetails");
        updateURL("recommendedPlanDetails");
      }
      return;
    }

    // Traditional login - requires OTP
    console.log("Traditional login - proceeding to OTP verification");
    setCurrentLoginData(loginData);

    // Clear Google processing state since we're navigating to OTP
    setIsProcessingGoogleLogin(false);

    setCurrentView("otpVerification");
    updateURL("otpVerification");
  };

  // Updated OTP verification success handler
  const handleOTPVerificationSuccess = (userData: UserData) => {
    console.log("=== OTP Verification Success (Traditional Login) ===");
    console.log("OTP verification successful:", userData);

    const token = currentLoginData?.access_token || "";
    setVerifiedUserId(userData.user_id);
    setVerifiedAccessToken(token);

    if (currentLoginData) {
      AuthStorage.setAuthData(currentLoginData.access_token, currentLoginData.refresh_token, userData.user_id, userData);
    } else {
      AuthStorage.setUserData(userData);
    }
    setCurrentLoginData(null);

    // Check user_fname to determine next step
    const userFirstName = userData.user_fname;
    console.log("User first name:", userFirstName);

    const resolvedPlan = resolveDirectPlan();
    if (!userFirstName || userFirstName.trim() === "") {
      // User doesn't have a first name - show registration form
      console.log("User name not set, showing registration form");
      setCurrentView("userRegistration");
      updateURL("userRegistration");
    } else {
      // User has a first name - go directly to destination based on selected plan
      console.log("User has name, navigating to destination");

      if ((resolvedPlan ?? selectedPlanDetails)?.issignature) {
        // Signature plan - go to expert form
        setCurrentView("expertForm");
        updateURL("expertForm");
      } else {
        // Regular plan - go to recommended plan details
        setCurrentView("recommendedPlanDetails");
        updateURL("recommendedPlanDetails");
      }
    }
  };

  // Handler for back from OTP
  const handleBackFromOTP = () => {
    setCurrentView("login");
    updateURL("login");
  };

  // Updated user registration success handler
  const handleUserRegistrationSuccess = (
    firstName: string,
    lastName: string,
  ) => {
    console.log("=== User Registration Success ===");
    console.log("User registration completed:", { firstName, lastName });

    // Update user data in auth storage
    const currentUserData = AuthStorage.getUserData();
    if (currentUserData) {
      const updatedUserData = {
        ...currentUserData,
        user_fname: firstName,
        user_lname: lastName,
      };
      AuthStorage.setUserData(updatedUserData);
    }

    // After registration, navigate based on selected plan type
    const resolvedPlan = resolveDirectPlan();
    if ((resolvedPlan ?? selectedPlanDetails)?.issignature) {
      // Signature plan - go to expert form
      console.log("Post-registration: navigating to expert form");
      setCurrentView("expertForm");
      updateURL("expertForm");
    } else {
      // Regular plan - go to recommended plan details
      console.log("Post-registration: navigating to recommended plan details");
      setCurrentView("recommendedPlanDetails");
      updateURL("recommendedPlanDetails");
    }
  };

  // Handler for back from registration
  const handleBackFromRegistration = () => {
    // Check if user came from Google login or OTP
    if (currentLoginData) {
      // Traditional login path - go back to OTP
      setCurrentView("otpVerification");
      updateURL("otpVerification");
    } else {
      // Google login path - go back to login
      setCurrentView("login");
      updateURL("login");
    }
  };

  // Handler for back from recommended plan details
  const handleBackFromRecommendedPlan = () => {
    setCurrentView("planDetails");
    if (selectedPlan) {
      updateURL("planDetails", selectedPlan);
    }
  };

  // Handler for payment success
  const handlePaymentSuccess = (
    planType: string,
    duration: number,
    paymentDetails: {
      orderId: string;
      paymentId: string;
      amount: number;
      transactionDate: string;
      apiPaymentId?: string;
      signatureId?: string;
      invoiceNumber?: string;
      userPaymentId?: number;
    },
  ) => {
    console.log("Payment success handler called with enhanced data:", {
      planType,
      duration,
      paymentDetails,
    });

    // Store enhanced payment success data
    setPaymentSuccessData({
      planType,
      duration,
      paymentDetails,
    });

    // Store additional data in localStorage as backup
    const additionalPaymentInfo = {
      invoiceNumber: paymentDetails.invoiceNumber,
      apiPaymentId: paymentDetails.apiPaymentId,
      signatureId: paymentDetails.signatureId,
      userPaymentId: paymentDetails.userPaymentId,
      planType,
      duration,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(
      "paymentSuccessBackup",
      JSON.stringify(additionalPaymentInfo),
    );

    // NEW: Set flag to trigger automatic invoice sending
    localStorage.setItem("autoSendInvoice", "true");

    // Navigate to payment success page
    setCurrentView("paymentSuccess");
    updateURL("paymentSuccess");
  };

  // Handler for back from payment success
  const handleBackFromPaymentSuccess = () => {
    setCurrentView("plans");
    updateURL("plans");
    // Clear payment success data and backup
    setPaymentSuccessData(null);
    localStorage.removeItem("paymentSuccessBackup");
    localStorage.removeItem("lastPaymentSuccess");
  };

  // Handler for get invoice from payment success page
  const handleGetInvoice = () => {
    if (paymentSuccessData?.paymentDetails?.invoiceNumber) {
      console.log(
        "Invoice requested for:",
        paymentSuccessData.paymentDetails.invoiceNumber,
      );
      alert(
        `Invoice #${paymentSuccessData.paymentDetails.invoiceNumber} will be sent to your registered email address.`,
      );
    } else {
      console.log("Invoice requested for payment:", paymentSuccessData);
      alert("Invoice will be sent to your registered email address.");
    }
  };
  const handleExpertFormSuccess = () => {
    console.log("Expert form submitted successfully");
    setCurrentView("signaturePlanSuccess");
    updateURL("signaturePlanSuccess");
  };

  // Update the handleBackFromExpertForm to handle success page as well:
  const handleBackFromSignatureSuccess = () => {
    setCurrentView("plans");
    updateURL("plans");
  };

  const getPlanCardData = (plan: PlanDetails) => {
    const staticConfig =
      staticPlansConfig[plan.id as keyof typeof staticPlansConfig];

    if (staticConfig) {
      return {
        icon: staticConfig.icon,
        colors: { bg: staticConfig.bgColor, border: staticConfig.borderColor },
      };
    }

    // Fallback for signature plan
    return {
      icon: <SignatureBgImageIcon className="w-[40px] h-[40px]" />,
      colors: { bg: "bg-[#F9FEFE]", border: "border-[#6DD0CF]" },
    };
  };

  // Helper function to get the lowest price for a plan
  const getLowestPriceForPlan = (plan: PlanDetails) => {
    if (!plan.pricingOptions || plan.pricingOptions.length === 0) {
      return null;
    }

    const lowestPriceOption = plan.pricingOptions.reduce((lowest, current) => {
      const currentPrice = parseFloat(current.price);
      const lowestPrice = parseFloat(lowest.price);
      return currentPrice < lowestPrice ? current : lowest;
    });

    return lowestPriceOption;
  };

  // Get current user ID for components
  const currentUserId = AuthStorage.getUserId() || userId;
  const currentUserData = AuthStorage.getUserData();

  if (loading && !directPurchasePlanId) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="w-full px-24 py-4 mt-4 3xl:mt-10 4xl:mt-16">
          <div className="flex items-center">
            <div className="w-[120px] h-[36px] 2xl:w-[120px] 3xl:w-[172px] 3xl:h-[48px] 4xl:w-[200px] 4xl:h-[73px] cursor-pointer">
              <Logo className="w-full h-full object-contain" />
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-24 py-6 2xl-py-10 min-h-[calc(100vh-160px)]">
          <div className="w-full flex gap-8 items-start justify-center">
            <div className="w-[40%] 2xl:w-1/2 relative min-h-[600px] flex items-center justify-center">
              <div className="absolute inset-0 flex items-center justify-center">
                <BgIllustration className="w-[540px] h-[560px] 2xl:h-[580px] 4xl:h-[800px]" />
              </div>
            </div>
            <div className="w-[60%] 2xl:w-1/2 pr-8">
              <div className="space-y-4 animate-pulse">
                <div className="h-20 bg-gray-200 rounded-2xl"></div>
                <div className="h-4 bg-gray-200 rounded w-48"></div>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-20 bg-gray-200 rounded-2xl"></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="w-full py-4">
          <div className="flex justify-center">
            <p className="text-center text-[12px] text-[#0000008C]">
              © 2026 - 2027 FitLife Fitness Foods LLP. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  if (error && allPlansData.length === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="w-full px-24 py-4 mt-4 3xl:mt-10 4xl:mt-16">
          <div className="flex items-center">
            <div className="w-[120px] h-[36px] 2xl:w-[120px] 3xl:w-[172px] 3xl:h-[48px] 4xl:w-[200px] 4xl:h-[73px] cursor-pointer">
              <Logo className="w-full h-full object-contain" />
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#202020] mb-2">
              Unable to Load Plans
            </h2>
            <p className="text-[#646464] mb-4">{error}</p>
            <button
              onClick={fetchAndPrepareAllPlans}
              className="bg-[#0DB5B5] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#0A9999] transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>

        <footer className="w-full py-4">
          <div className="flex justify-center">
            <p className="text-center text-[12px] text-[#0000008C]">
              © 2026 - 2027 FitLife Fitness Foods LLP. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <>
      {/* Handle signature success page as full screen */}
      {currentView === "signaturePlanSuccess" ? (
        <TrivaSignaturePlanSuccessPage
          onBackToPlans={handleBackFromSignatureSuccess}
        />
      ) : currentView === "paymentSuccess" ? (
        /* NEW: Handle payment success page as full screen */
        <PaymentSuccessPageDesktop
          planType={paymentSuccessData?.planType}
          duration={paymentSuccessData?.duration}
          paymentDetails={paymentSuccessData?.paymentDetails}
          onGetInvoice={handleGetInvoice}
          onBackToPlans={handleBackFromPaymentSuccess}
        />
      ) : (
        <div className="min-h-screen bg-white flex flex-col overflow-hidden">
          {/* Header */}
          <div className="w-full px-24 py-4 mt-4 3xl:mt-10 4xl:mt-16">
            <div className="flex items-center justify-between">
              <div className="w-[120px] h-[36px] 2xl:w-[120px] 3xl:w-[172px] 3xl:h-[48px] 4xl:w-[200px] 4xl:h-[73px] cursor-pointer">
                <Logo className="w-full h-full object-contain" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center px-4 3xl:px-24 py-4 2xl-py-8 min-h-[calc(100vh-200px)]">
            <div className="w-full flex gap-0 3xl:gap-12 items-start justify-center">
              {/*  Left Section - Background Illustration */}
              {currentView !== "login" &&
                currentView !== "otpVerification" &&
                currentView !== "userRegistration" &&
                currentView !== "recommendedPlanDetails" && (
                  <div className="w-[40%] 2xl:w-1/2 relative min-h-[600px] flex items-center justify-center">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BgIllustration className="w-[540px] h-[560px] 2xl:h-[580px] 4xl:h-[800px]" />
                    </div>
                  </div>
                )}
              {/* Right Section - Content Area */}
              <div className="w-[60%] 2xl:w-1/2 pr-8">
                {currentView === "login" ? (
                  <div className="w-full max-w-lg mx-auto">
                    <LoginForm
                      onBack={handleBackFromLogin}
                      onLoginSuccess={handleLoginSuccess}
                      onOTPRequired={handleOTPRequired}
                      onGoogleLoginSuccess={handleGoogleLoginSuccess}
                      isGoogleProcessing={isProcessingGoogleLogin}
                      onGoogleProcessingStart={() =>
                        setIsProcessingGoogleLogin(true)
                      }
                    />
                  </div>
                ) : currentView === "otpVerification" ? (
                  <div className="w-full max-w-lg mx-auto">
                    {currentLoginData?.isGoogleAuthenticated ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                        <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-blue-700">
                          Processing Google login...
                        </p>
                      </div>
                    ) : (
                      <OTPVerification
                        contactInfo={currentLoginData?.contactInfo}
                        contactType={currentLoginData?.contactType}
                        userId={currentLoginData?.user_id}
                        expectedOTP={currentLoginData?.otp}
                        onBack={handleBackFromOTP}
                        onVerificationSuccess={handleOTPVerificationSuccess}
                      />
                    )}
                  </div>
                ) : currentView === "userRegistration" ? (
                  <div className="w-full max-w-lg mx-auto">
                    <UserRegistration
                      onSubmit={handleUserRegistrationSuccess}
                      onBack={handleBackFromRegistration}
                      initialFirstName={
                        AuthStorage.getUserData()?.user_fname ||
                        (currentLoginData as any)?.googleUserInfo?.firstName ||
                        ""
                      }
                      initialLastName={
                        AuthStorage.getUserData()?.user_lname ||
                        (currentLoginData as any)?.googleUserInfo?.lastName ||
                        ""
                      }
                      userId={verifiedUserId ?? AuthStorage.getUserId()}
                      accessToken={verifiedAccessToken || AuthStorage.getAccessToken()}
                    />
                  </div>
                ) : currentView === "recommendedPlanDetails" &&
                  selectedPlanDetails ? (
                  <div className="w-full max-w-2xl mx-auto">
                    <RecommendedPlanDetailsForDesktop
                      planType={
                        selectedPlanDetails.id as
                          | "lite"
                          | "spark"
                          | "ignite"
                          | "thrive"
                          | "signature"
                      }
                      userId={currentUserId}
                      onBack={handleBackFromRecommendedPlan}
                      userData={currentUserData || undefined}
                      preFilledEmail={googleUserEmail || undefined}
                      onPaymentSuccess={handlePaymentSuccess}
                      selectedPlanData={{
                        id: selectedPlanDetails.id,
                        title: selectedPlanDetails.title,
                        subtitle: selectedPlanDetails.subtitle,
                        description: selectedPlanDetails.description,
                        pricingOptions: selectedPlanDetails.pricingOptions,
                        whatYouGet: selectedPlanDetails.whatYouGet,
                        whatsNotIncluded: selectedPlanDetails.whatsNotIncluded ?? [],
                        featuresTable: selectedPlanDetails.featuresTable,
                      }}
                      selectedDuration={
                        selectedPricingOptionFromDetails || undefined
                      }
                      preSelectedPrice={
                        selectedPricingOptionFromDetails
                          ? (() => {
                              const option =
                                selectedPlanDetails.pricingOptions.find(
                                  (opt) =>
                                    opt.duration ===
                                    selectedPricingOptionFromDetails,
                                );
                              return option
                                ? parseFloat(option.price)
                                : undefined;
                            })()
                          : undefined
                      }
                    />
                  </div>
                ) : currentView === "recommendedPlanDetails" &&
  !selectedPlanDetails ? (
  <AutoRedirect
    onRedirect={() => {
      setCurrentView("plans");
      updateURL("plans");
    }}
  />
) : currentView === "expertForm" ? (
                  <ExpertConnectionForm
                    userId={currentUserId}
                    onBack={handleBackFromExpertForm}
                    onSuccess={handleExpertFormSuccess}
                  />
                ) : currentView === "planDetails" && selectedPlanDetails ? (
                  <PlanDetailsScreen
                    planData={selectedPlanDetails}
                    userId={currentUserId}
                    onBack={handleBackToPlans}
                    onConnectWithExperts={handleConnectWithExperts}
                    onBuyNow={handleBuyNow}
                    setSelectedPricingOptionFromDetails={
                      setSelectedPricingOptionFromDetails
                    }
                  />
                ) : (
                  <div className="w-full justify-center">
                    {/* Recommended Plans Section */}
                    <div>
                      <h2 className="text-xl font-medium text-black mb-4">
                        Recommended plans
                      </h2>

                      {/* All Four Plans */}
                      {["lite", "spark", "ignite", "thrive"].map((planId) => {
                        const plan = allPlansData.find((p) => p.id === planId);
                        if (!plan) return null;

                        const cardData = getPlanCardData(plan);
                        const lowestPrice = getLowestPriceForPlan(plan);

                        return (
                          <PlanCard
                            key={plan.id}
                            icon={cardData.icon}
                            title={plan.title}
                            description={plan.subtitle}
                            bgColor={cardData.colors.bg}
                            borderColor={cardData.colors.border}
                            issignature={false}
                            onSelect={() => handlePlanSelect(plan.id)}
                            price={lowestPrice?.price}
                            originalPrice={lowestPrice?.originalPrice}
                          />
                        );
                      })}

                      {/* Error handling */}
                      {error && allPlansData.length > 0 && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start">
                            <AlertCircle className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              <p className="text-yellow-800 font-medium">
                                Limited information available
                              </p>
                              <p className="text-yellow-700">
                                Some plan details may not be current. Click "Try
                                Again" to reload.
                              </p>
                              <button
                                onClick={fetchAndPrepareAllPlans}
                                className="mt-2 text-yellow-800 underline hover:text-yellow-900 text-sm"
                              >
                                Try Again
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="border-[0.5px] border-[#ebebeb] mt-6"></div>
                    </div>
                    {/* Signature Plan */}
                    <div className="pt-6">
                      {(() => {
                        const cardData = getPlanCardData(signaturePlan);
                        return (
                          <PlanCard
                            icon={cardData.icon}
                            title={signaturePlan.title}
                            description={signaturePlan.subtitle}
                            bgColor={cardData.colors.bg}
                            borderColor={cardData.colors.border}
                            issignature={true}
                            onSelect={() => handlePlanSelect("signature")}
                          />
                        );
                      })()}
                      {/* Contact Us Section */}
                      <div className="mt-4 text-center">
                        <p className="text-[12px] font-medium font-inter text-[#646464]">
                          Still have questions? We're here to help!{" "}
                          <a
                            href="https://mytriva.com/contact-us/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#007E7F] hover:underline cursor-pointer font-medium"
                          >
                            Contact us
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="w-full py-4">
            <div className="flex justify-center">
              <p className="text-center text-[12px] text-[#0000008C]">
                © 2026 - 2027 FitLife Fitness Foods LLP. All rights reserved.
              </p>
            </div>
          </footer>
        </div>
      )}
    </>
  );
};
export default TrivaPlansDesktop;